/**
 * Financial Attribution Ledger — Core Types
 * Every dollar in the FIE is traceable to a source record.
 */

export interface FinancialAttribution {
  id: string;
  sourceType: "job" | "invoice" | "material_order" | "labor_log" | "permit" | "assumption";
  sourceId: string;
  sourceTable: string;
  category: "revenue" | "materials" | "labor" | "subcontractor" | "permits" | "overhead" | "contingency";
  amount: number;
  isActual: boolean;
  description: string;
  jobId?: string;
  createdAt: string;
}

export interface AttributionBreakdown {
  byJob: Record<string, FinancialAttribution[]>;
  byCategory: Record<string, FinancialAttribution[]>;
  bySourceType: Record<string, FinancialAttribution[]>;
  totalActual: number;
  totalEstimated: number;
  total: number;
}

/** Deterministic hash of all attribution records for fingerprint alignment */
export function computeAttributionDigest(attributions: FinancialAttribution[]): string {
  const sorted = [...attributions].sort((a, b) => a.id.localeCompare(b.id));
  const serialized = sorted
    .map(a => `${a.id}:${a.sourceType}:${a.sourceId}:${a.category}:${a.amount}:${a.isActual}`)
    .join("|");
  // DJB2a hash
  let hash = 5381;
  for (let i = 0; i < serialized.length; i++) {
    hash = ((hash << 5) + hash) ^ serialized.charCodeAt(i);
    hash |= 0;
  }
  return `ATTR-${Math.abs(hash).toString(16).toUpperCase().padStart(8, "0")}`;
}

/** Group attributions into a breakdown view */
export function getAttributionBreakdown(
  attributions: FinancialAttribution[],
  entityType?: "job" | "category" | "sourceType",
  entityId?: string,
): AttributionBreakdown {
  let filtered = attributions;
  if (entityType === "job" && entityId) {
    filtered = attributions.filter(a => a.jobId === entityId);
  } else if (entityType === "category" && entityId) {
    filtered = attributions.filter(a => a.category === entityId);
  } else if (entityType === "sourceType" && entityId) {
    filtered = attributions.filter(a => a.sourceType === entityId);
  }

  const byJob: Record<string, FinancialAttribution[]> = {};
  const byCategory: Record<string, FinancialAttribution[]> = {};
  const bySourceType: Record<string, FinancialAttribution[]> = {};
  let totalActual = 0;
  let totalEstimated = 0;

  for (const a of filtered) {
    const jk = a.jobId || "_no_job";
    if (!byJob[jk]) byJob[jk] = [];
    byJob[jk].push(a);

    if (!byCategory[a.category]) byCategory[a.category] = [];
    byCategory[a.category].push(a);

    if (!bySourceType[a.sourceType]) bySourceType[a.sourceType] = [];
    bySourceType[a.sourceType].push(a);

    if (a.isActual) totalActual += a.amount;
    else totalEstimated += a.amount;
  }

  return { byJob, byCategory, bySourceType, totalActual, totalEstimated, total: totalActual + totalEstimated };
}
